import { csrf_headers, get_token, get_user_id, getCookie, is_demo_domain } from '@/router/index.js';
import { get_domain_style } from '@/main';
import { PAGE_LOADER_RESTORED_EVENT, show_page_loader, hide_page_loader } from '@/utils/page_loader';
import { SYSTEM_MANAGEMENT_ENDPOINTS, build_system_management_url } from '@/constants/system_management_endpoints.js';
import { PLAYGROUND_PLATFORM_KEYS } from '@/utils/playground_platform_launch';
import { shutdown_push_notifications } from '@/services/push_notification_service';

export default {
  name: 'SidebarView',

  data: () => ({
    orcaa_url: process.env.VUE_APP_ORCAA_URL,
    proba_workflows_url: process.env.VUE_APP_PROBA_WORKFLOWS_URL,
    complyon_url: process.env.VUE_APP_COMPLYON_URL,
    engagenet_url: process.env.VUE_APP_ENGAGENET_URL,
    x_verdict_url: process.env.VUE_APP_X_VERDICT_URL,
    proba_analytics_url: process.env.VUE_APP_PROBA_ANALYTICS_URL,

    user: {},
    is_admin: false,
    is_collapsed: false,
    settings_menu_open: false,
    is_internal_suite: false,
    is_demo_aic_one_domain: false,
    logo_url: null,
    allowed_app_keys: [],      // controls launcher visibility
    platform_access_loading: false,
    launching_platform: false,
    unread_chat_count: Number(window.__one_chat_unread_count || 0),
  }),

  computed: {
    styleConfig() {
      return get_domain_style();
    },

    is_drive_route() {
      return ['Drive', 'Chat', 'Ziva'].includes(this.$route?.name) || ['/drive', '/chat', '/ziva'].includes(this.$route?.path);
    },

    should_show_logout() {
      return Boolean(get_token()) && !is_demo_domain() && localStorage.getItem('is_playground_user') !== '1';
    },

  },

  mounted() {
    const host = (window.location.hostname || "").toLowerCase();
    this.is_internal_suite =
    host === "1campus.aic-one.com" ||
    host.includes("1campus") ||
    host.includes("1campus");

    this.csrf_token = getCookie('csrftoken');

     // Load saved collapse state from localStorage
    const saved_state = localStorage.getItem('sidebarCollapsed');
    if (saved_state !== null) {
      this.is_collapsed = saved_state === 'true';
    }

    this.sync_settings_menu_with_route(this.$route);

    // Apply initial body class
    this.update_body_class();

    // Restore from localStorage for instant rendering
    const stored_user = localStorage.getItem('user');
    const stored_is_admin = localStorage.getItem('is_admin');

    if (stored_user) {
      this.user = JSON.parse(stored_user);
    }
    if (stored_is_admin !== null) {
      this.is_admin = stored_is_admin === 'true';
    }

    // Refresh user details in background
    this.get_user_details();
    this.init_branding();
    this.load_allowed_app_keys();
    this._platform_poll = setInterval(() => {
      this.load_allowed_app_keys();
    }, 15000);
    this._page_loader_restored_handler = () => {
      this.launching_platform = false;
    };
    window.addEventListener('one-chat-unread-count', this.handle_chat_unread_count);
    window.addEventListener(PAGE_LOADER_RESTORED_EVENT, this._page_loader_restored_handler);

  },

  beforeUnmount() {
    document.body.classList.remove('sidebar-collapsed');
    document.body.classList.remove('drive-sidebar-collapsed');

    if (this._platform_poll) {
      clearInterval(this._platform_poll);
      this._platform_poll = null;
    }
    window.removeEventListener('one-chat-unread-count', this.handle_chat_unread_count);
    window.removeEventListener(PAGE_LOADER_RESTORED_EVENT, this._page_loader_restored_handler);
    this._page_loader_restored_handler = null;
  },

  methods: {
    handle_chat_unread_count(event) {
      this.unread_chat_count = Number(event?.detail?.unread_count || 0);
    },

    async init_branding() {
      if (!this.$branding) return;

      const branding_data = this.$branding.get_branding();
      if (branding_data.logos?.logo) {
        this.logo_url = branding_data.logos.logo;
      }

      if (!branding_data.initialized && this.user?.company_id) {
        const org_id = this.user.company_id || this.user.organization_id;
        if (org_id) {
          const loaded = await this.$branding.load_branding(org_id);
          if (loaded?.logos?.logo) {
            this.logo_url = loaded.logos.logo;
          }
        }
      }
    },

    get_sidebar_logo_src() {
      return this.logo_url || this.styleConfig.logo;
    },

    handle_sidebar_logo_error(e) {
      e.target.src = this.styleConfig.logo;
    },

    get_launch_loader_logo_src() {
      return this.styleConfig.loader || this.get_sidebar_logo_src();
    },

    toggle_settings_menu() {
      this.settings_menu_open = !this.settings_menu_open;
    },
    is_bootstrap_mobile() {
      return window.matchMedia('(max-width: 575.98px)').matches;
    },
    is_settings_route(route = this.$route) {
      const settings_paths = [
        '/profile',
        '/permission',
        '/role',
        '/settings',
        '/access_control',
        '/system_configure',
        '/superset_manager',
        '/main_settings'
      ];

      return settings_paths.includes(route?.path);
    },
    sync_settings_menu_with_route() {
      if (this.is_bootstrap_mobile()) return;

      if (this.is_drive_route) {
        this.settings_menu_open = false;
      }
    },
    close_settings_menu_on_mobile() {
      if (this.is_bootstrap_mobile()) {
        this.settings_menu_open = false;
      }
    },
    toggle_collapse() {
      this.is_collapsed = !this.is_collapsed;
      // Save state to localStorage
      localStorage.setItem('sidebarCollapsed', this.is_collapsed);

      // Update body class for main content adjustment
      this.update_body_class();

      // Emit event to parent component if needed
      this.$emit('sidebar-toggled', this.is_collapsed);
    },
    update_body_class() {
      if (this.is_collapsed) {
        document.body.classList.add('sidebar-collapsed');
      } else {
        document.body.classList.remove('sidebar-collapsed');
      }

      if (this.is_drive_route) {
        document.body.classList.add('drive-sidebar-collapsed');
      } else {
        document.body.classList.remove('drive-sidebar-collapsed');
      }
    },
    async logout() {
      const url = process.env.VUE_APP_API_BASE_URL + '/aic_one_api/system_management/user_logout/';
      const user_id = get_user_id();
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      try {
        await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-Page-URL': window.location.href,
            'X-CSRFToken': this.csrf_token
          },

        body: JSON.stringify({ user_id })
        });

        shutdown_push_notifications('logout');
        localStorage.clear();
        this.$router.push('/');
      } catch (error) {
        this.$notyf.info('Logout Failed');

      }
    },

    get_platform_label(platform) {
      const labels = Object.freeze({
        'x-verdict': 'X-Verdict',
        'orcaa': 'Orcaa',
        'proba': 'Proba Analytics',
        'engagenet': this.is_internal_suite ? 'IC Workbook' : 'EngageNet',
        'workflow': 'Proba Workflows',
        'complyon': 'ComplyOn',
      });

      const desc = Object.getOwnPropertyDescriptor(labels, platform);
      return (desc && desc.enumerable && typeof desc.value === 'string') ? desc.value : 'platform';
    },

    _safe_lookup(obj, key) {
      const desc = Object.getOwnPropertyDescriptor(obj, key);
      return (desc && desc.enumerable) ? desc.value : undefined;
    },

    playground_platform_id(key) {
      const alias_map = Object.freeze({
        __proto__: null,
        'x-verdict': ['x-verdict', 'x verdict', 'xverdict'],
        'orcaa': ['orcaa', 'orcaa.ai'],
        'proba': ['proba', 'proba analytics'],
        'engagenet': ['engagenet', 'engage net', 'ic workbook'],
        'workflow': ['workflow', 'workflows', 'proba workflows', 'proba-workflows'],
        'complyon': ['complyon', 'comply on'],
      });
      const names = this._safe_lookup(alias_map, key) || [key];

      try {
        const platforms = JSON.parse(localStorage.getItem('playground_platforms') || '[]');
        const platform = Array.isArray(platforms)
          ? platforms.find((item) => names.includes(String(item?.name || '').trim().toLowerCase()))
          : null;
        return platform?.id || null;
      } catch {
        return null;
      }
    },

    is_playground_user() {
      return localStorage.getItem('is_playground_user') === '1';
    },

    get_playground_platform_session_context(key) {
      return ({
        context_id: localStorage.getItem('playground_context_id'),
        platform_id: this.playground_platform_id(key),
      });
    },

    validate_playground_platform_session_context(context) {
      if (context.context_id && context.platform_id) return true;
      this.$notyf.error('Playground context is not ready yet');
      return false;
    },

    build_playground_platform_session_headers(token) {
      return ({
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Token ${token}` } : {}),
        ...csrf_headers(this.csrf_token),
      });
    },

    build_playground_platform_session_payload(context) {
      return ({
        context_id: context.context_id,
        platform_id: context.platform_id,
      });
    },

    is_expired_playground_platform_response(response, body) {
      return response.status === 409 || body.status === 'expired';
    },

    handle_expired_playground_platform_session(body) {
      this.$notyf.error(body.message || 'Your Playground session has expired');
      this.$router.push({ name: 'Login' });
      return false;
    },

    handle_playground_platform_session_response(response, body) {
      if (this.is_expired_playground_platform_response(response, body)) {
        return this.handle_expired_playground_platform_session(body);
      }

      if (!response.ok || body.status !== 'success') {
        this.$notyf.error(body.message || 'Unable to prepare this platform');
        return false;
      }

      return this.handle_playground_platform_session_success(body);
    },

    handle_playground_platform_session_success(body) {
      const redirect_url = body.data?.redirect_url;
      if (redirect_url) {
        window.location = redirect_url;
        return false;
      }

      return true;
    },

    async prepare_playground_platform_session(key) {
      if (!this.is_playground_user()) return true;

      const context = this.get_playground_platform_session_context(key);
      if (!this.validate_playground_platform_session_context(context)) return false;

      try {
        const token = get_token();
        const response = await fetch(
          build_system_management_url(SYSTEM_MANAGEMENT_ENDPOINTS.prepare_playground_platform_session),
          {
            method: 'POST',
            credentials: 'include',
            headers: this.build_playground_platform_session_headers(token),
            body: JSON.stringify(this.build_playground_platform_session_payload(context)),
          },
        );
        const body = await response.json();
        return this.handle_playground_platform_session_response(response, body);
      } catch {
        this.$notyf.error('Unable to prepare this platform');
        return false;
      }
    },

    async launch_platform(plaform) {
      if (this.launching_platform) return;

      const token = get_token();

       if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }
      const user_id = get_user_id();
      const platform = String(plaform || '').toLowerCase();

      const is_valid_key = /^[a-zA-Z0-9_-]+$/.test(platform);
      if (!is_valid_key) {
        this.$notyf.error('Unknown platform: invalid key');
        return;
      }

      const platform_session_ready = await this.prepare_playground_platform_session(platform);
      if (!platform_session_ready) {
        return;
      }

      const endpoint_map = Object.freeze({
      'x-verdict': '/aic_one_api/system_management/launch_xverdict/',
      'orcaa': '/aic_one_api/system_management/launch_orcaa/',
      'proba': '/aic_one_api/system_management/launch_proba/',
      'engagenet': '/aic_one_api/system_management/launch_engagenet/',
      'workflow': '/aic_one_api/system_management/launch_proba_workflow/',
      'complyon': '/aic_one_api/system_management/launch_complyon/',
      'complyon_onboard': '/aic_one_api/system_management/launch_complyon_onboard/',
      });

      const desc = Object.getOwnPropertyDescriptor(endpoint_map, platform);
      const path = (desc && desc.enumerable && typeof desc.value === 'string') ? desc.value : null;

      if (!path) {
        this.$notyf.error(`Unknown platform: ${platform}`);
        return;
      }

      const api_url = `${process.env.VUE_APP_API_BASE_URL}${path}`;
      this.launching_platform = true;
      show_page_loader(`Launching ${this.get_platform_label(platform)}...`, this.get_launch_loader_logo_src());

      try {
        const res = await fetch(api_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Token ${token}` } : {}),
            'X-Page-URL': window.location.href,
            ...csrf_headers(this.csrf_token),
          },
          body: JSON.stringify({ user_id })
        });

        // Parse JSON once
        const data = await res.json();

        if (!res.ok) {
          let msg = data.message || 'Failed to launch';

          // ---- CLEAN DUPLICATE WORDS ---
          msg = msg.replace(/\b(\w+)\s+\1\b/gi, "$1");
          msg = msg.replace(/\bLaunch\s+Launch\b/gi, "Launch");
          this.$notyf.error(msg);
          this.launching_platform = false;
          hide_page_loader();
          return;
        }

        // Handle success paths
        const redirect_url = data.redirect_url || data.data?.redirect_url;
        if (data.status === 'success' && redirect_url) {
          window.location = redirect_url;
        } else {
          this.$notyf.error('Failed to launch platform');
          this.launching_platform = false;
          hide_page_loader();
        }

      } catch (error) {
        this.$notyf.error('Failed to launch');
        this.launching_platform = false;
        hide_page_loader();
      }

    },


    async get_user_details() {
      const api_url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_current_user/`;
      const token = get_token();

      if (!token) {
        this.$router.push({ name: 'Login' });
        return;
      }

      try {
        const response = await fetch(api_url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            ...csrf_headers(this.csrf_token)
          },
        });

        if (response.ok) {
          const response_data = await response.json();
          this.user = response_data.data;
          this.is_admin = this.user && this.user.role && this.user.role.toLowerCase().includes('admin');
          this.sync_settings_menu_with_route(this.$route);
          this.init_branding();

          // Persist to localStorage
          localStorage.setItem('user', JSON.stringify(this.user));
          localStorage.setItem('is_admin', this.is_admin);
        } else {
          this.$notyf.info('Error fetching user');
        }
      } catch (error) {
        this.$notyf.info('Error fetching user');
      }
    },

    async load_allowed_app_keys() {
    this.platform_access_loading = true;

    try {
      if (localStorage.getItem('is_playground_user') === '1') {
        this.allowed_app_keys = [...PLAYGROUND_PLATFORM_KEYS];
        return;
      }

      const token = this.get_token_safe();
      if (!token) {
        this.allowed_app_keys = [];
        return;
      }

      const role = String(localStorage.getItem("role") || "").toLowerCase();
      if (role.includes("super admin")) {
        this.allowed_app_keys = ["orcaa", "proba", "x-verdict", "engagenet", "workflow", "complyon", "complyon_onboard"];
        return;
      }

      const user_id = this.user?.id || this.user?.user_id || null;
      if (!user_id) {
        this.allowed_app_keys = [];
        return;
      }
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/api/get_user_platform_access_api/?user_id=${user_id}`;

      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`,
        },
      });

      const payload = await res.json().catch(() => null);

      if (!res.ok || payload?.status !== "success") {
        this.allowed_app_keys = [];
        return;
      }

      const rows = payload?.data || [];
      const enabled_names = rows
        .filter(r => String(r.status || "").toUpperCase() === "ENABLED")
        .map(r => r.platform_name)
        .filter(Boolean);

      this.allowed_app_keys = this.map_platform_names_to_app_keys(enabled_names);

    } catch (e) {
      console.error("Failed to load platform access:", e);
      this.allowed_app_keys = [];
    } finally {
      this.platform_access_loading = false;
    }
  },

  normalize_platform_name(name) {
    return String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[._-]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  },

  map_platform_names_to_app_keys(platform_names) {
    const name_to_key = Object.freeze(Object.assign(Object.create(null), {
      "orcaa ai": "orcaa",
      "orcaa": "orcaa",

      "proba analytics": "proba",
      "proba": "proba",
      "analytics": "proba",

      "complyon": "complyon",

      "engagenet": "engagenet",
      "ic workbook": "engagenet",

      "x verdict": "x-verdict",
      "xverdict": "x-verdict",

      "proba workflows": "workflow",
      "workflows": "workflow",

      "complyon onboard": "complyon_onboard",
      "complyon_onboard": "complyon_onboard",
    }));

    const keys = [];

    (platform_names || []).forEach((name) => {
      const normalized = this.normalize_platform_name(name);

      if (Reflect.has(name_to_key, normalized)) {
        const mapped_key = Reflect.get(name_to_key, normalized);

        if (typeof mapped_key === "string" && !keys.includes(mapped_key)) {
          keys.push(mapped_key);
        }
      }
    });

    return keys;
  },

  get_token_safe() {
    return get_token();
  },
  },

  watch: {
    $route(to) {
      this.sync_settings_menu_with_route(to);
      this.update_body_class();
    },
    '$branding.logos.logo': {
      handler(new_logo) {
        if (new_logo) {
          this.logo_url = new_logo;
        }
      },
      deep: true
    }
  }
};
